// oxlint-disable jsx-no-new-object-as-prop

import { EASE } from "@nnf3d/lib";
import type { GLOBAL_SETTINGS_QUERY_RESULT } from "@nnf3d/studio/types";
import type { PortableTextComponents } from "@portabletext/react";
import { PortableText } from "@portabletext/react";
import { useProgress } from "@react-three/drei";
import { useLocation } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { AnimatePresence, m } from "motion/react";
import { useCallback, useEffect, useState } from "react";

import { useGlobal } from "../lib/global-context";
import { bgLoop, mouseClickEnter, useGlobalSound } from "../lib/hooks/use-sound";
import { framesFiredStore, loadStore, modelRevealStore } from "../lib/store";
import type { VoidComponent } from "../lib/types";
import { LoaderMask } from "./loader-mask";
import { LoaderVideos } from "./loader-videos";

const components = {
	block: {
		normal: ({ children, index }) => (
			<m.h1
				className="font-serif text-display-small desktop:text-display-medium"
				initial={{ opacity: 0, y: "2rem", scale: 0.8 }}
				animate={{
					opacity: 1,
					y: "0",
					transition: { duration: 2, ease: EASE.OUT_EXPO, delay: 0.2 + index * 0.25 },
				}}
				exit={{
					opacity: 0,
					y: "0.5rem",
					scale: 0.8,
					transition: { duration: 1, ease: EASE.IN_EXPO },
				}}
			>
				{children}
			</m.h1>
		),
	},
	marks: { strong: ({ children }) => <span className="font-sans font-semibold">{children}</span> },
} as const satisfies PortableTextComponents<
	NonNullable<GLOBAL_SETTINGS_QUERY_RESULT[number]>["mainLoaderHeadline"][number]
>;

const Loader: VoidComponent = () => {
	const location = useLocation();
	const isIndexPage = location.pathname === "/" && location.search?.about !== 1;
	const {
		loaderButtonText,
		mainLoaderHeadline,
		mainLoaderTexts,
		contextualLoaderHeadline,
		contextualLoaderTexts,
	} = useGlobal();
	const { progress, active } = useProgress();
	const { muted } = useGlobalSound();

	const { isStarted, isCreated, isLoaded } = useStore(loadStore);
	const framesFired = useStore(framesFiredStore);

	useEffect(() => {
		if (active || progress < 100) {
			return;
		}

		loadStore.setState((state) => ({ ...state, isLoaded: true }));
	}, [active, progress, isCreated]);

	const { revealedCount, registeredCount } = useStore(modelRevealStore, (state) => ({
		revealedCount: state.revealed.size,
		registeredCount: state.registered.size,
	}));

	// Calculate reveal progress as percentage (0-10 to fill the remaining 10%)
	const revealProgress =
		registeredCount > 0 ? Math.round((revealedCount / registeredCount) * 10) : 0;
	const isRevealComplete = registeredCount > 0 && revealedCount >= registeredCount;

	const setIsStarted = useCallback(() => {
		if (loadStore.state.isStarted || !isRevealComplete) {
			return;
		}

		loadStore.setState((state) => ({ ...state, isStarted: true }));

		bgLoop.play();
	}, [isRevealComplete]);

	const handlePointerDown = useCallback(() => {
		if (!muted) {
			mouseClickEnter.play();
		}
	}, [muted]);

	const [loaderTextsIndex, setLoaderTextsIndex] = useState(0);

	useEffect(() => {
		if (isStarted) {
			return (): void => {
				/* empty */
			};
		}

		const interval = setInterval(() => {
			setLoaderTextsIndex(
				(prev) =>
					(prev + 1) % (isIndexPage ? mainLoaderTexts.length : contextualLoaderTexts.length),
			);
		}, 3000);

		return (): void => {
			clearInterval(interval);
		};
	}, [isStarted, isIndexPage, mainLoaderTexts, contextualLoaderTexts]);

	return (
		<AnimatePresence mode="wait">
			{!isStarted && (
				<m.div
					className="fixed inset-0 z-110 flex flex-col items-center justify-center"
					initial={{ scale: 0.8, opacity: 1 }}
					animate={{ scale: 1, opacity: 1, transition: { duration: 2, ease: EASE.OUT_EXPO } }}
					exit={{ scale: 1.5, opacity: 0, transition: { duration: 1, ease: EASE.IN_EXPO } }}
					onClick={setIsStarted}
					onPointerDown={handlePointerDown}
				>
					<div className="absolute -inset-200 bg-surface-primary" />

					<div className="pointer-events-none absolute inset-0 blur-xl">
						<LoaderVideos />
					</div>

					<LoaderMask isRevealComplete={isRevealComplete} progress={progress} />

					<div className="relative my-6 flex max-w-220 flex-1 flex-col items-center justify-between gap-2 px-4 text-center text-foreground desktop:my-24">
						<div>
							<PortableText
								value={isIndexPage ? mainLoaderHeadline : contextualLoaderHeadline}
								components={components}
							/>
						</div>

						<div className="flex flex-col items-center justify-center gap-4 desktop:gap-8">
							<m.div
								initial={{ opacity: 0 }}
								animate={{
									opacity: 1,
									transition: { duration: 2, ease: EASE.OUT_EXPO, delay: 0.3 },
								}}
								exit={{ opacity: 0, transition: { duration: 1, ease: EASE.IN_EXPO } }}
							>
								<AnimatePresence mode="wait">
									{(isIndexPage ? mainLoaderTexts : contextualLoaderTexts).map(
										(text, index) =>
											loaderTextsIndex === index && (
												<m.p
													key={text}
													className="max-w-[60ch] font-serif text-sm desktop:text-md"
													variants={{
														initial: { opacity: 0, y: "1rem" },
														animate: {
															opacity: 1,
															y: "0",
															transition: { duration: 2, ease: EASE.OUT_EXPO },
														},
														exit: {
															opacity: 0,
															y: "-0.5rem",
															transition: { duration: 1, ease: EASE.IN_EXPO },
														},
													}}
													initial="initial"
													animate="animate"
													exit="exit"
												>
													{text}
												</m.p>
											),
									)}
								</AnimatePresence>
							</m.div>

							<m.button
								type="button"
								className="group pointer-events-auto desktop:w-fit"
								inert={!isRevealComplete}
								onClick={setIsStarted}
								onPointerDown={handlePointerDown}
								initial={{ opacity: 0 }}
								animate={{
									opacity: 1,
									transition: { duration: 2, ease: EASE.OUT_EXPO, delay: 0.4 },
								}}
								exit={{ opacity: 0, transition: { duration: 1, ease: EASE.IN_EXPO } }}
							>
								<div className="flex h-12 cursor-pointer items-center justify-center bg-white px-5.5 text-foreground desktop:h-12">
									<span className="group-inert:opacity-0">{loaderButtonText}</span>

									<span className="absolute opacity-0 group-inert:opacity-100">
										{`${Math.min(
											99,
											Math.round(
												progress * 0.9 + Math.max(framesFired, isLoaded ? revealProgress : 0),
											),
										)}%`}
									</span>
								</div>
							</m.button>
						</div>
					</div>
				</m.div>
			)}
		</AnimatePresence>
	);
};

export { Loader };
